import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH8_QUESTIONS } from "../data/neetPhysicsCh8Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 8: Gravitation (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Newton's Law of Gravitation", "Acceleration Due to Gravity", "Gravitational Energy & Escape Velocity", "Satellites & Kepler's Laws"];

const SECTION_COLORS = {
  "Newton's Law of Gravitation":               "#2563eb",
  "Acceleration Due to Gravity":                "#ea580c",
  "Gravitational Energy & Escape Velocity":     "#7c3aed",
  "Satellites & Kepler's Laws":                 "#0d9488",
};

const CONCEPTS = [
  // ── Newton's Law of Gravitation ────────────────────────────────────────────
  { section: "Newton's Law of Gravitation", title: "Newton's universal law of gravitation",
    explanation: "Every particle in the universe attracts every other particle with a force that is directly proportional to the product of their masses and inversely proportional to the SQUARE of the distance between them, acting along the line joining them: F = Gm1m2/r^2. This is the same force that makes an apple fall and keeps the Moon in orbit -- it is universal.",
    example: `F = G . m1 . m2 / r^2

Two 1 kg masses, 1 m apart:
F = (6.674x10^-11)(1)(1) / (1)^2 = 6.674x10^-11 N

(Extremely tiny -- gravity between everyday objects is
negligible unless one mass is astronomically large, like Earth)` },
  { section: "Newton's Law of Gravitation", title: "The universal gravitational constant G",
    explanation: "G is a fundamental constant of nature with the same value everywhere in the universe: G = 6.674x10^-11 N.m^2.kg^-2 (often rounded to 6.67x10^-11). It was first measured experimentally by Henry Cavendish in 1798 using a torsion balance, which also allowed the mass of the Earth to be calculated for the first time.",
    example: `G = 6.674 x 10^-11 N.m^2.kg^-2

Dimensional formula of G (from F = G m1 m2 / r^2):
G = F.r^2 / (m1.m2)
  = [M L T^-2][L^2] / [M^2]
  = [M^-1 L^3 T^-2]` },
  { section: "Newton's Law of Gravitation", title: "Properties of the gravitational force",
    explanation: "The gravitational force is always ATTRACTIVE (never repulsive), acts along the line joining the two masses (a CENTRAL force), obeys Newton's third law (equal and opposite on each mass), and is completely independent of the medium between the masses -- placing water, glass, or any material between two masses does not change the gravitational force between them.",
    example: `Force is unaffected by intervening medium:
F (in vacuum) = F (through water) = F (through glass)
      = G.m1.m2 / r^2   (medium plays no role at all)` },
  { section: "Newton's Law of Gravitation", title: "Principle of superposition of gravitational forces",
    explanation: "When a mass is acted upon by several other masses simultaneously, the net gravitational force on it is the VECTOR SUM of the individual forces due to each mass taken separately (as if the others were absent). Direction matters just as much as magnitude when combining these forces.",
    example: `Test mass at origin; two other masses produce:
F1 = 4F along +x-axis
F2 = 3F along +y-axis   (perpendicular to F1)

Net force = sqrt(F1^2 + F2^2) = sqrt(16F^2 + 9F^2)
          = sqrt(25F^2) = 5F   (3-4-5 vector triangle)` },

  // ── Acceleration Due to Gravity ─────────────────────────────────────────────
  { section: "Acceleration Due to Gravity", title: "Relation between g and G — acceleration due to gravity at the surface",
    explanation: "Equating the weight of a surface object (mg) to the gravitational force exerted on it by the planet (GMm/R^2) gives g = GM/R^2. This links the everyday, measurable acceleration g to the universal constant G and the planet's mass M and radius R -- and lets us calculate a planet's mass once g, R and G are known.",
    example: `mg = GMm/R^2  =>  g = GM/R^2  =>  M = gR^2/G

Earth: g = 9.8 m/s^2, R = 6.4x10^6 m, G = 6.67x10^-11
M = (9.8)(6.4x10^6)^2 / (6.67x10^-11)
  = (9.8)(4.096x10^13) / (6.67x10^-11)
  ~ 6.0 x 10^24 kg   (Earth's actual mass)` },
  { section: "Acceleration Due to Gravity", title: "Variation of g with altitude (height)",
    explanation: "Moving away from the planet's surface, gravity weakens following the inverse-square law: g_h = gR^2/(R+h)^2 exactly. For heights much smaller than the radius (h << R), the binomial approximation gives g_h ~ g(1 - 2h/R) -- g decreases roughly linearly with height near the surface.",
    example: `Exact:   g_h = g . R^2 / (R+h)^2
Approx (h<<R): g_h ~ g(1 - 2h/R)

At h = R (not small!) use the EXACT formula:
g_h = g . R^2/(2R)^2 = g/4   (drops to one-quarter of g)` },
  { section: "Acceleration Due to Gravity", title: "Variation of g with depth",
    explanation: "Below the surface, only the mass enclosed within a sphere of radius (R-d) contributes to the gravitational pull (the shell outside contributes zero net force, by Newton's shell theorem). For a planet of uniform density, this gives g_d = g(1 - d/R) -- a LINEAR decrease with depth, reaching zero at the center.",
    example: `g_d = g(1 - d/R)     (uniform density assumed)

At d = R/2 (halfway to the center):
g_d = g(1 - 0.5) = g/2 = 4.9 m/s^2

At d = R (the center): g_d = g(1-1) = 0` },
  { section: "Acceleration Due to Gravity", title: "Effect of Earth's rotation and latitude on g",
    explanation: "Earth's rotation means an object at latitude lambda needs some centripetal force, which slightly reduces the NET (effective) gravity measured: g_eff = g - omega^2.R.cos^2(lambda). This reduction is ZERO at the poles (lambda=90 deg, cos=0) and MAXIMUM at the equator (lambda=0 deg, cos=1) -- so measured gravity is smallest at the equator and largest at the poles.",
    example: `g_eff = g - omega^2.R.cos^2(lambda)
omega = 2.pi/T = 2.pi / 86400 s ~ 7.27x10^-5 rad/s

At the equator (lambda=0): reduction = omega^2.R
  = (7.27x10^-5)^2 (6.4x10^6) ~ 0.034 m/s^2 less than at the poles` },

  // ── Gravitational Energy & Escape Velocity ──────────────────────────────────
  { section: "Gravitational Energy & Escape Velocity", title: "Gravitational potential and potential energy",
    explanation: "Taking potential energy to be zero at infinite separation (the natural reference point, since gravity vanishes there), the gravitational PE of a mass m at distance r from mass M is U = -GMm/r -- always NEGATIVE for finite r. Gravitational potential (PE per unit mass) is V = -GM/r. The negative sign signals a BOUND system: energy must be supplied to pull the masses apart to infinity.",
    example: `U = -G.M.m / r    (zero reference at infinity)

Two masses 5 kg and 10 kg, r = 2 m:
U = -(6.674x10^-11)(5)(10) / 2 = -1.67 x 10^-9 J
(negative -- system is bound; work must be done to separate them)` },
  { section: "Gravitational Energy & Escape Velocity", title: "Work done against gravity between two points",
    explanation: "The work done moving a mass m from distance r1 to a farther distance r2 (r2 > r1) equals the change in potential energy: W = U(r2) - U(r1) = GMm(1/r1 - 1/r2). Since 1/r1 > 1/r2 whenever r2 > r1, this work is POSITIVE -- energy must be supplied to move the mass farther from the planet.",
    example: `W = G.M.m (1/r1 - 1/r2)

Raise 1000 kg satellite from surface (r1=R) to h=R (r2=2R):
W = GMm/R - GMm/2R = GMm/2R = m.(GM/R)/2 = m.g.R/2
W = 1000 x 9.8 x 6.4x10^6 / 2 ~ 3.14 x 10^10 J` },
  { section: "Gravitational Energy & Escape Velocity", title: "Escape velocity",
    explanation: "Escape velocity is the minimum speed needed to launch an object from a planet's surface so it just barely escapes the planet's gravity, reaching infinity with zero leftover kinetic energy. Setting total energy at launch to zero, (1/2)mv_e^2 - GMm/R = 0, gives v_e = sqrt(2GM/R) = sqrt(2gR) -- independent of the escaping object's own mass.",
    example: `v_e = sqrt(2GM/R) = sqrt(2gR)

Earth: g=9.8 m/s^2, R=6.4x10^6 m
v_e = sqrt(2 x 9.8 x 6.4x10^6) = sqrt(1.2544x10^8)
    ~ 1.12 x 10^4 m/s = 11.2 km/s` },
  { section: "Gravitational Energy & Escape Velocity", title: "Relation between escape velocity and orbital velocity",
    explanation: "For a satellite orbiting very close to a planet's surface, orbital velocity is v_o = sqrt(GM/R). Comparing this to escape velocity v_e = sqrt(2GM/R) shows they are always related by a fixed factor: v_e = sqrt(2) . v_o -- escape velocity is about 41% larger than the corresponding orbital velocity, regardless of the planet.",
    example: `v_o = sqrt(GM/R)        v_e = sqrt(2GM/R) = sqrt(2).v_o

Earth: v_o (near surface) ~ 7.9 km/s
v_e = sqrt(2) x 7.9 ~ 1.414 x 7.9 ~ 11.2 km/s   (checks out)` },

  // ── Satellites & Kepler's Laws ───────────────────────────────────────────────
  { section: "Satellites & Kepler's Laws", title: "Orbital velocity and time period of a satellite",
    explanation: "For a satellite in a circular orbit of radius r, gravity supplies exactly the centripetal force needed: GMm/r^2 = mv_o^2/r, giving orbital velocity v_o = sqrt(GM/r). The time to complete one orbit is T = 2.pi.r / v_o = 2.pi.sqrt(r^3/GM) -- larger orbits mean both slower speeds AND longer periods.",
    example: `v_o = sqrt(GM/r)      T = 2.pi.sqrt(r^3 / GM)

Satellite at r = 2R (height h = R), Earth:
v_o = sqrt(GM/2R) = sqrt(gR/2) ~ sqrt(9.8x6.4x10^6/2) ~ 5.6 km/s
T = 2.pi.r/v_o = 2.pi(1.28x10^7)/5600 ~ 1.44x10^4 s ~ 4.0 hours` },
  { section: "Satellites & Kepler's Laws", title: "Geostationary satellites",
    explanation: "A geostationary satellite appears fixed above one point on Earth's equator. This requires: (1) a period of exactly 24 hours (matching Earth's rotation), (2) an orbit in the equatorial plane, and (3) motion west-to-east, the same direction Earth spins. Solving T = 24 h in T^2 = 4.pi^2.r^3/GM gives an orbital radius of about 42,000 km from Earth's center -- a height of roughly 36,000 km above the surface.",
    example: `T^2 = 4.pi^2.r^3 / GM ,  T = 24 h = 86400 s

Solving for r: r ~ 4.22 x 10^7 m ~ 42,000 km (from Earth's center)
Height above surface = r - R ~ 42,000 - 6,400 ~ 36,000 km` },
  { section: "Satellites & Kepler's Laws", title: "Energy of an orbiting satellite",
    explanation: "An orbiting satellite of mass m at radius r has kinetic energy KE = (1/2)mv_o^2 = GMm/2r and potential energy PE = -GMm/r. Its total mechanical energy is E = KE + PE = -GMm/2r -- always NEGATIVE, confirming the satellite is gravitationally bound. The binding energy (energy needed to free it to infinity) is the positive quantity +GMm/2r.",
    example: `KE = +GMm/2r     PE = -GMm/r     E_total = -GMm/2r

Binding energy = -E_total = +GMm/2r

Satellite m=1000 kg at r=2R (h=R, Earth):
E_total = -GMm/(2x1.28x10^7) ~ -1.57 x 10^10 J
Binding energy ~ +1.57 x 10^10 J` },
  { section: "Satellites & Kepler's Laws", title: "Kepler's three laws of planetary motion",
    explanation: "Kepler's THREE laws describe planetary orbits: (1) Law of orbits -- each planet moves in an ELLIPSE with the Sun at one focus; (2) Law of areas -- the line joining a planet to the Sun sweeps out EQUAL areas in EQUAL times (a consequence of conserved angular momentum, so planets move faster near the Sun, slower far away); (3) Law of periods -- T^2 is proportional to r^3 (r = the orbit's semi-major axis).",
    example: `Law of areas: dA/dt = constant (angular momentum conserved)
Law of periods: T^2 / r^3 = constant (same for every planet)

If planet's r = 4 x Earth's r (Earth: T=1 yr, r=1 AU):
T^2 = r^3 = 4^3 = 64  =>  T = sqrt(64) = 8 years` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh8Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh8Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh8Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH8_QUESTIONS : NEET_PHYSICS_CH8_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 8</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Gravitation</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH8_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH8_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
